// Single source of truth for the API base URL the SPA hits.
//
// In local dev: import.meta.env.PROD === false, so we use relative `/api/*`
// URLs and rely on the Vite dev proxy (vite.config.ts) to forward to
// localhost:3001.
//
// In any deployed env (hub.dev / hub.stage / hub.sjglobal.com.au): the SPA also
// uses relative `/api/*` URLs because CloudFront proxies `/api/*` to API
// Gateway. So in both cases relative URLs work and we never need to read
// VITE_API_BASE.
//
// VITE_API_BASE is provided in .env.example only for the rare case someone
// wants to test the prod SPA against a different API origin (CORS pre-flight
// would need to allow it on the Lambda side too).

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export interface ApiError extends Error {
  status: number;
  body?: unknown;
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const url = API_BASE ? `${API_BASE}${path}` : path;
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    ...init
  });

  let body: unknown = undefined;
  const text = await res.text();
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }

  if (!res.ok) {
    const err = new Error(`API ${res.status} ${res.statusText}`) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return body as T;
}

export function navigateTo(path: string): void {
  const url = API_BASE ? `${API_BASE}${path}` : path;
  window.location.href = url;
}
